import { useState, useCallback, useEffect } from "react"
import {
  FaMapMarkerAlt,
  FaUniversity,
  FaUpload,
  FaClipboardCheck,
  FaUser,
} from "react-icons/fa"
import type { EmployeeOnboardingDetailsDto } from "@/modules/employee/employee/dto/employeeDetails"
import {
  employeeProfileSchema,
  addressSchema,
  bankDetailsSchema,
} from "../../employee/schemas/employeeFormSchemas"
import { ProfileDetailsForm } from "./EmployeePersonalDetailsForm"
import { AddressDetailsForm } from "./AddressDetailsForm"
import { BankDetailsForm } from "./BankDetailsForm"
import { DocumentsUploadForm } from "./DocumentsUploadForm"
import { ReviewSubmitForm } from "./ReviewSubmitForm"
import { EmployeeOnboardingMultiStepFormContainer } from "./EmployeeOnboardingMultiStepFormContainer"
import type { Step } from "./EmployeeOnboardingMultiStepFormHeader"
import { useEmployeeOnboarding } from "../../employee/hooks/employeeHook"
import { Onboardingstatus } from "@/modules/onboarding/onboarding-status"
import { toast } from "sonner"

const steps: Step[] = [
  { id: 0, title: "User Details", icon: FaUser },
  { id: 1, title: "Address", icon: FaMapMarkerAlt },
  { id: 2, title: "Bank Details", icon: FaUniversity },
  { id: 3, title: "Documents", icon: FaUpload },
  { id: 4, title: "Review", icon: FaClipboardCheck },
]

interface EmployeeSelfOnboardingFormProps {
  onboardingData?: EmployeeOnboardingDetailsDto | null
  initialStep?: number
}

export default function EmployeeSelfOnboardingForm({
  onboardingData,
  initialStep = 0,
}: EmployeeSelfOnboardingFormProps) {
  const {
    isSubmitting,
    isSavingData,
    submitOnboarding,
    loadOnboardingData,
    updateOnboardingData,
  } = useEmployeeOnboarding()

  const [currentStep, setCurrentStep] = useState(initialStep)
  const [stepValidity, setStepValidity] = useState<boolean[]>(
    Array(steps.length).fill(false),
  )
  const [stepVisited, setStepVisited] = useState<boolean[]>(
    Array(steps.length).fill(false),
  )
  const [formData, setFormData] = useState<EmployeeOnboardingDetailsDto>({
    onboardingId: "",
    onboardingStatus: "",
    seenWelcome: false,
    inviteSent: false,
    notes: [],
    requiredDocuments: [],
    onboardingCreatedAt: "",
    onboardingUpdatedAt: "",
    onboardingStartDate: "",
    latest: false,
    previousOnboardingId: "",
    employeeId: "",
    identityType: "",
    identityNumber: "",
    firstName: "",
    secondName: "",
    lastName: "",
    email: "",
    phone: "",
    position: "",
    employeeStatus: "",
    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    },
    bankDetails: {
      bankName: "",
      accountNumber: "",
      branchCode: "",
      accountHolderName: "",
      accountType: "",
    },
    employeeStartDate: "",
    dateOfBirth: "",
    gender: "",
    taxNumber: "",
    raceGroup: "",
    maritalStatus: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    employeeCreatedAt: "",
    employeeUpdatedAt: "",
    organizationId: "",
    organizationName: "",
    fieldFlags: [],
    documentsToBeSigned: [],
  })
  const [response, setResponse] = useState<string | null>(null)
  const [requiredDocFiles, setRequiredDocFiles] = useState<Map<string, File>>(
    new Map(),
  )
  const [signedDocFiles, setSignedDocFiles] = useState<Map<string, File>>(
    new Map(),
  )
  const handleDocumentsChange = useCallback(
    (requiredDocs: Map<string, File>, signedDocs: Map<string, File>) => {
      setRequiredDocFiles(requiredDocs)
      setSignedDocFiles(signedDocs)
    },
    [],
  )

  useEffect(() => {
    if (onboardingData) {
      setFormData((prev) => ({
        ...prev,
        ...onboardingData,
        address: onboardingData.address ?? prev.address,
        bankDetails: onboardingData.bankDetails ?? prev.bankDetails,
      }))

      const profileValid = employeeProfileSchema.safeParse({
        firstName: onboardingData.firstName,
        secondName: onboardingData.secondName,
        lastName: onboardingData.lastName,
        idType: onboardingData.identityType,
        idNumber: onboardingData.identityNumber,
        gender: onboardingData.gender,
        dateOfBirth: onboardingData.dateOfBirth,
        phone: onboardingData.phone,
        email: onboardingData.email,
        position: onboardingData.position,
        emergencyContactName: onboardingData.emergencyContactName,
        emergencyContactPhone: onboardingData.emergencyContactPhone,
        taxNumber: onboardingData.taxNumber,
        raceGroup: onboardingData.raceGroup,
      }).success

      const address =
        typeof onboardingData.address === "string" ? {} : onboardingData.address
      const addressValid = addressSchema.safeParse(address).success

      const bankDetails =
        typeof onboardingData.bankDetails === "string"
          ? {}
          : onboardingData.bankDetails
      const bankValid = bankDetailsSchema.safeParse(bankDetails).success

      const requiredDocs = onboardingData.requiredDocuments ?? []
      const requiredDocUploads = onboardingData.requiredDocumentUploads ?? []
      const docsToBeSigned = onboardingData.documentsToBeSigned ?? []
      const hasRequiredDocs =
        requiredDocs.length === 0 ||
        requiredDocs.every((docType) =>
          requiredDocUploads.some(
            (u) => u.documentName === docType && u.submitted,
          ),
        )
      const hasSignedDocs =
        docsToBeSigned.length === 0 ||
        docsToBeSigned.filter((d) => !d.hasSigned).length === 0
      const docsValid = hasRequiredDocs && hasSignedDocs

      setStepValidity([profileValid, addressValid, bankValid, docsValid, false])
    }
  }, [onboardingData])

  const updateFormData = useCallback(
    (data: Partial<EmployeeOnboardingDetailsDto>) => {
      setFormData((prev) => ({ ...prev, ...data }))
    },
    [],
  )

  const handleStepValidityChange = useCallback(
    (stepIndex: number, isValid: boolean) => {
      setStepValidity((prev) => {
        const newValidity = [...prev]
        newValidity[stepIndex] = isValid
        return newValidity
      })
    },
    [],
  )

  const isFormValid =
    stepValidity[0] && stepValidity[1] && stepValidity[2] && stepValidity[3]

  const nextStep = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setStepVisited((prev) => {
        const newVisited = [...prev]
        newVisited[currentStep] = true
        return newVisited
      })

      if (
        onboardingData?.onboardingStatus !== Onboardingstatus.COMPLETED &&
        onboardingData?.onboardingStatus !== Onboardingstatus.SUBMITTED
      ) {
        updateOnboardingData(formData)
      }
      setCurrentStep(currentStep + 1)
    }
  }, [
    currentStep,
    formData,
    updateOnboardingData,
    onboardingData?.onboardingStatus,
  ])

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setStepVisited((prev) => {
        const newVisited = [...prev]
        newVisited[currentStep] = true
        return newVisited
      })
      if (
        onboardingData?.onboardingStatus !== Onboardingstatus.COMPLETED &&
        onboardingData?.onboardingStatus !== Onboardingstatus.SUBMITTED
      ) {
        updateOnboardingData(formData)
      }
      setCurrentStep(currentStep - 1)
    }
  }, [
    currentStep,
    formData,
    updateOnboardingData,
    onboardingData?.onboardingStatus,
  ])

  const handleStepChange = useCallback(
    (stepIndex: number) => {
      if (stepIndex === currentStep) return
      setStepVisited((prev) => {
        const newVisited = [...prev]
        newVisited[currentStep] = true
        return newVisited
      })
      setCurrentStep(Math.min(Math.max(stepIndex, 0), steps.length - 1))
    },
    [currentStep],
  )

  const handleSubmit = async () => {
    if (!isFormValid) {
      toast.error("Please complete all required fields before submitting.")
      return
    }

    try {
      const result = await submitOnboarding(
        formData,
        requiredDocFiles,
        signedDocFiles,
      )
      setResponse(result.message || "Onboarding completed successfully!")
      toast.success(result.message || "Onboarding completed successfully!")
      loadOnboardingData()
    } catch (error) {
      const message =
        typeof error === "string"
          ? error
          : error instanceof Error
            ? error.message
            : "Submission failed. Please try again."
      setResponse(message)
      toast.error(message)
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <ProfileDetailsForm
            formData={formData}
            onUpdate={updateFormData}
            onValidityChange={(isValid) => handleStepValidityChange(0, isValid)}
            isReadOnly={
              onboardingData?.onboardingStatus === Onboardingstatus.COMPLETED ||
              onboardingData?.onboardingStatus === Onboardingstatus.SUBMITTED
            }
          />
        )
      case 1:
        return (
          <AddressDetailsForm
            formData={formData.address}
            onUpdate={(address) => updateFormData({ address })}
            onValidityChange={(isValid) => handleStepValidityChange(1, isValid)}
            isReadOnly={
              onboardingData?.onboardingStatus === Onboardingstatus.COMPLETED ||
              onboardingData?.onboardingStatus === Onboardingstatus.SUBMITTED
            }
            fieldFlags={formData.fieldFlags}
          />
        )
      case 2:
        return (
          <BankDetailsForm
            formData={formData.bankDetails}
            onUpdate={(bankDetails) => updateFormData({ bankDetails })}
            onValidityChange={(isValid) => handleStepValidityChange(2, isValid)}
            isReadOnly={
              onboardingData?.onboardingStatus === Onboardingstatus.COMPLETED ||
              onboardingData?.onboardingStatus === Onboardingstatus.SUBMITTED
            }
            fieldFlags={formData.fieldFlags}
          />
        )
      case 3:
        return (
          <DocumentsUploadForm
            requiredDocuments={formData.requiredDocuments}
            requiredDocumentUploads={formData.requiredDocumentUploads}
            documentsToBeSigned={formData.documentsToBeSigned}
            onFileChange={() => {}}
            onValidityChange={(isValid) => handleStepValidityChange(3, isValid)}
            onDocumentsChange={handleDocumentsChange}
            requiredDocFiles={requiredDocFiles}
            signedDocFiles={signedDocFiles}
            isReadOnly={
              onboardingData?.onboardingStatus === Onboardingstatus.COMPLETED ||
              onboardingData?.onboardingStatus === Onboardingstatus.SUBMITTED
            }
            fieldFlags={formData.fieldFlags}
          />
        )
      case 4:
        return (
          <ReviewSubmitForm
            formData={formData}
            response={response}
            onClearResponse={() => setResponse(null)}
            requiredDocFiles={requiredDocFiles}
            signedDocFiles={signedDocFiles}
            onboardingDocs={{
              requiredDocuments: formData.requiredDocuments,
              documentsToBeSigned: formData.documentsToBeSigned,
            }}
            isReadOnly={
              onboardingData?.onboardingStatus === Onboardingstatus.COMPLETED ||
              onboardingData?.onboardingStatus === Onboardingstatus.SUBMITTED
            }
          />
        )
      default:
        return null
    }
  }

  const stepDescriptions: Record<number, string> = {
    0: "Please provide your personal information",
    1: "Enter your residential address details",
    2: "Provide your banking information",
    3: "Upload required documents",
    4: "Review your information before submitting",
  }

  return (
    <EmployeeOnboardingMultiStepFormContainer
      steps={steps}
      currentStep={currentStep}
      stepStatus={stepValidity}
      stepVisited={stepVisited}
      onStepChange={handleStepChange}
      onPrevious={prevStep}
      onNext={nextStep}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      isSaving={isSavingData}
      isReadOnly={
        onboardingData?.onboardingStatus === Onboardingstatus.COMPLETED ||
        onboardingData?.onboardingStatus === Onboardingstatus.SUBMITTED
      }
      stepDescriptions={stepDescriptions}
      submitLabel="Submit"
      canSubmit={isFormValid}
    >
      {renderStepContent()}
    </EmployeeOnboardingMultiStepFormContainer>
  )
}
